import React from 'react'
import Auth from '@aws-amplify/auth'
import api from 'balkerne-core/api'
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import BackgroundImage from '../images/login-background.jpg'
import webLogo from '../images/logo_web.png'

async function getUserDetails() {
  return await api
    .get('/users/me')
    .then(res => {
      return res.data.data
    })
    .catch(err => {
      console.error(err)
      return null
    })
}

type ResetPasswordProps = {
  history: any
}

type ResetPasswordState = {
  email: string
  v_code: string
  password_new: string
  password_confirm: string
  step: number
  errMessage: string
  infoMessage: string
  isLoading: boolean
  [key: string]: any
}

export class ResetPassword extends React.Component<ResetPasswordProps, ResetPasswordState> {
  constructor(props) {
    super(props)
    this.state = {
      email: '',
      v_code: '',
      password_new: '',
      password_confirm: '',
      step: 1,
      errMessage: '',
      infoMessage: '',
      isLoading: false,
    }
  }

  normalizeEmail = () => {
    const { email } = this.state
    if (email && email.length > 3 && !email.toLowerCase().endsWith('arcusfm.com')) {
      return email.toLowerCase()
    }
    return email
  }

  validateForm = () => {
    return !!(
      this.state.v_code &&
      this.state.password_new &&
      this.state.v_code.length >= 3 &&
      this.state.password_new.length > 7
    )
  }

  validateEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return this.state.email.length > 0 && emailRegex.test(this.state.email.toLowerCase())
  }

  handleChange = event => {
    this.setState({
      [event.target.name]: event.target.value,
      errMessage: '',
      infoMessage: '',
    })
  }

  sendVerificationCode = async event => {
    event.preventDefault()
    if (this.state.isLoading) return

    const email = this.normalizeEmail()

    if (!this.validateEmail()) {
      this.setState({ errMessage: 'Enter the email address you use to sign in.' })
      return
    }

    this.setState({ isLoading: true, errMessage: '', infoMessage: '' })

    try {
      await Auth.forgotPassword(email)
      this.setState({
        email,
        step: 2,
        errMessage: '',
        infoMessage: `We sent a verification code to ${email}.`,
      })
    } catch (err: any) {
      console.error(err)
      this.setState({ errMessage: err?.message ?? 'Unable to send a verification code.' })
    } finally {
      this.setState({ isLoading: false })
    }
  }

  changePassword = async event => {
    event.preventDefault()
    if (this.state.isLoading) return

    const email = this.normalizeEmail()

    if (this.state.password_new !== this.state.password_confirm) {
      this.setState({ errMessage: 'Passwords do not match, please try again.' })
      return
    }

    if (!this.validateForm()) {
      this.setState({ errMessage: 'Please ensure the password meets the criteria below.' })
      return
    }

    this.setState({ isLoading: true, errMessage: '', infoMessage: '' })

    try {
      await Auth.forgotPasswordSubmit(email, this.state.v_code, this.state.password_new)
      await Auth.signIn(email, this.state.password_new)
      await getUserDetails()

      Object.keys(sessionStorage).forEach(key => {
        if (key.includes('Cognito')) {
          sessionStorage.removeItem(key)
        }
      })

      this.setState({
        step: 3,
        errMessage: '',
        infoMessage: 'Your password has been updated. You can now sign in with your new password.',
        isLoading: false,
        v_code: '',
        password_new: '',
        password_confirm: '',
      })
    } catch (err: any) {
      this.setState({
        errMessage: err?.message ?? 'Unable to reset your password.',
        isLoading: false,
      })
    }
  }

  backToEmailStep = () => {
    this.setState({
      step: 1,
      v_code: '',
      password_new: '',
      password_confirm: '',
      errMessage: '',
      infoMessage: '',
      isLoading: false,
    })
  }

  renderNotice() {
    const { errMessage, infoMessage } = this.state

    if (errMessage) {
      return <Alert severity="error">{errMessage}</Alert>
    }

    if (infoMessage) {
      return <Alert severity="success">{infoMessage}</Alert>
    }

    return null
  }

  renderStepContent() {
    const { email, v_code, password_new, password_confirm, step, isLoading } = this.state

    if (step === 1) {
      return (
        <Stack component="form" spacing={2} autoComplete="on" onSubmit={this.sendVerificationCode}>
          <Typography variant="h4" component="h1">
            Reset Password
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter the email address you use to sign in and we&apos;ll send you a verification code.
          </Typography>
          <TextField
            autoFocus
            label="Email"
            type="email"
            id="username"
            name="email"
            autoComplete="username"
            required
            fullWidth
            value={email}
            disabled={isLoading}
            onChange={this.handleChange}
          />
          <LoadingButton loading={isLoading} variant="contained" size="large" fullWidth type="submit">
            Send Verification Code
          </LoadingButton>
          <Button variant="text" fullWidth type="button" onClick={() => this.props.history.push('/')}>
            Back to Login
          </Button>
        </Stack>
      )
    }

    if (step === 2) {
      return (
        <Stack component="form" spacing={2} autoComplete="on" onSubmit={this.changePassword}>
          <Typography variant="h4" component="h1">
            Check Your Email
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter the verification code we sent to <strong>{email}</strong>, then choose a new password.
          </Typography>
          <TextField
            autoFocus
            label="Verification code"
            type="text"
            id="one-time-code"
            name="v_code"
            autoComplete="one-time-code"
            required
            fullWidth
            value={v_code}
            disabled={isLoading}
            onChange={this.handleChange}
          />
          <TextField
            label="New password"
            type="password"
            id="new-password"
            name="password_new"
            autoComplete="new-password"
            required
            fullWidth
            value={password_new}
            disabled={isLoading}
            onChange={this.handleChange}
          />
          <TextField
            label="Confirm new password"
            type="password"
            id="confirm-new-password"
            name="password_confirm"
            autoComplete="new-password"
            required
            fullWidth
            value={password_confirm}
            disabled={isLoading}
            onChange={this.handleChange}
          />
          <Stack
            component="ul"
            spacing={0.5}
            sx={{
              m: 0,
              pl: 3,
              color: 'text.secondary',
            }}>
            <Typography component="li" variant="body2">
              Longer than 7 characters
            </Typography>
            <Typography component="li" variant="body2">
              Includes uppercase and lowercase letters
            </Typography>
            <Typography component="li" variant="body2">
              Includes at least one number
            </Typography>
          </Stack>
          <LoadingButton loading={isLoading} variant="contained" size="large" fullWidth type="submit">
            Change Password
          </LoadingButton>
          <Button variant="text" fullWidth type="button" onClick={this.backToEmailStep}>
            Use a Different Email
          </Button>
          <Button variant="text" fullWidth type="button" onClick={this.sendVerificationCode}>
            Resend Verification Code
          </Button>
        </Stack>
      )
    }

    return (
      <Stack spacing={2}>
        <Typography variant="h4" component="h1">
          Password Updated
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your password has been changed successfully. Return to login and sign in with your new password.
        </Typography>
        <LoadingButton variant="contained" size="large" fullWidth onClick={() => this.props.history.push('/')}>
          Back to Login
        </LoadingButton>
      </Stack>
    )
  }

  render() {
    return (
      <Box
        sx={{
          overflow: 'hidden',
          position: 'relative',
        }}>
        <Box
          component="img"
          src={BackgroundImage}
          sx={{
            '@keyframes slow-pan': {
              '0%': {
                transform: 'translate(-5%, -5%) scale(1) rotate(-5deg)',
              },
              '100%': {
                transform: 'translate(-15%, -15%) scale(1.15) rotate(15deg)',
              },
            },
            animation: 'slow-pan 30s cubic-bezier(.21,0,.7,1) infinite',
            animationDelay: '-5s',
            animationDirection: 'alternate',
            position: 'absolute',
            top: 0,
            left: 0,
            minWidth: '120vw',
            minHeight: '120vh',
            zIndex: 0,
          }}
        />

        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{
            minHeight: '100vh',
            width: '100vw',
            backgroundColor: '#000000AD',
            p: 2,
            zIndex: 1,
            position: 'relative',
          }}>
          <Paper sx={{ width: '100%', maxWidth: 420, p: 4 }}>
            <Stack spacing={3}>
              <Stack alignItems="center" spacing={1}>
                <Box component="img" src={webLogo} alt="SmartResilience" sx={{ width: 280, height: 'auto' }} />
              </Stack>
              {this.renderNotice()}
              {this.renderStepContent()}
            </Stack>
          </Paper>
        </Stack>
      </Box>
    )
  }
}

export default ResetPassword
